'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, ArrowLeftRight, RotateCcw, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { getTransactionSummary } from '@/lib/transactions';

interface AuditRowProps {
  label: string;
  count: number;
  amount: number;
  color: string;
  icon: React.ReactNode;
  barColor: string;
  total: number;
}

function AuditRow({ label, count, amount, color, icon, barColor, total }: AuditRowProps) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-3 border-b border-border last:border-0">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-600 text-foreground">{label}</span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground font-mono-nums">
              ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
            <span className={`text-sm font-700 font-mono-nums ${color.includes('success') ? 'text-success' : color.includes('danger') ? 'text-danger' : color.includes('warning') ? 'text-warning' : color.includes('accent') ? 'text-accent' : 'text-muted-foreground'}`}>
              {count}
            </span>
          </div>
        </div>
        <div className="h-1.5 bg-border rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function TransactionTypeAudit() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const data = await getTransactionSummary(user.id);
      setSummary(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-5 flex items-center justify-center min-h-[200px]">
        <Loader2 size={20} className="text-accent animate-spin" />
      </div>
    );
  }

  const counts = summary?.typeCounts ?? { income: 0, expense: 0, transfer: 0, refund: 0 };
  const totalIncome = summary?.totalIncome ?? 0;
  const totalExpenses = summary?.totalExpenses ?? 0;
  const totalTransfers = summary?.totalTransfers ?? 0;
  const totalRefunds = summary?.totalRefunds ?? 0;
  const totalCount = counts.income + counts.expense + counts.transfer + counts.refund;
  const noIncome = counts.income === 0;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-base font-700 text-foreground">Transaction Type Audit</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {totalCount} total transactions classified
          </p>
        </div>
        <div className="flex items-center gap-2">
          {noIncome && totalCount > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-600 text-warning bg-warning/10 border border-warning/30 rounded-lg px-3 py-1.5">
              <AlertTriangle size={13} />
              No income detected
            </span>
          )}
          {totalCount === 0 && (
            <span className="text-xs text-muted-foreground">No transactions yet</span>
          )}
        </div>
      </div>

      {/* Warning banner */}
      {noIncome && totalCount > 0 && (
        <div className="mx-5 mt-4 flex items-start gap-3 bg-warning/10 border border-warning/30 rounded-lg px-4 py-3">
          <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-600 text-warning">Income = 0 — Classification may be incorrect</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Deposits and additions should be classified as Income unless they are transfers from your own account.
              Check that Direct Deposits, Settlements, and Zelle received from businesses are marked as Income.
            </p>
          </div>
        </div>
      )}

      {/* Audit rows */}
      <div className="px-5 pb-2 pt-2">
        <AuditRow
          label="Income"
          count={counts.income}
          amount={totalIncome}
          color="bg-success/15 text-success"
          icon={<TrendingUp size={15} />}
          barColor="bg-success"
          total={totalCount}
        />
        <AuditRow
          label="Expense"
          count={counts.expense}
          amount={totalExpenses}
          color="bg-danger/15 text-danger"
          icon={<TrendingDown size={15} />}
          barColor="bg-danger"
          total={totalCount}
        />
        <AuditRow
          label="Transfer"
          count={counts.transfer}
          amount={totalTransfers}
          color="bg-primary/20 text-muted-foreground"
          icon={<ArrowLeftRight size={15} />}
          barColor="bg-muted-foreground"
          total={totalCount}
        />
        <AuditRow
          label="Refund"
          count={counts.refund}
          amount={totalRefunds}
          color="bg-accent/15 text-accent"
          icon={<RotateCcw size={15} />}
          barColor="bg-accent"
          total={totalCount}
        />
      </div>

      {/* Footer note */}
      <div className="px-5 py-3 border-t border-border bg-card/50">
        <p className="text-xs text-muted-foreground">
          <span className="font-600 text-foreground">Rules: </span>
          Direct deposit / settlement / Zelle from business = Income · Own account transfer = Transfer · Debit card credit = Refund · Purchases = Expense
        </p>
      </div>
    </div>
  );
}
